import { useEffect } from "react";

/**
 * Lightweight hook to set document title + meta/OG tags per-page.
 * No external deps — uses direct DOM manipulation and cleans up on unmount.
 *
 * @param {Object} meta
 * @param {string} [meta.title]          Page title (appended with " | Kinora")
 * @param {string} [meta.description]    Meta description
 * @param {string} [meta.image]          OG image URL (poster / backdrop)
 * @param {string} [meta.url]            Canonical URL (defaults to window.location.href)
 */
export function useDocumentMeta({ title, description, image, url } = {}) {
  useEffect(() => {
    const siteName = "Kinora";
    const fullTitle = title ? `${title} | ${siteName}` : siteName;
    const canonicalUrl = url || window.location.href;

    const prev = {
      title: document.title,
      desc: getMeta("description"),
      ogTitle: getMeta("og:title", true),
      ogDesc: getMeta("og:description", true),
      ogImage: getMeta("og:image", true),
      ogUrl: getMeta("og:url", true),
      ogSite: getMeta("og:site_name", true),
    };

    document.title = fullTitle;
    if (description) setMeta("description", description);
    setOg("og:title", fullTitle);
    if (description) setOg("og:description", description);
    if (image) setOg("og:image", image);
    setOg("og:url", canonicalUrl);
    setOg("og:site_name", siteName);

    return () => {
      document.title = prev.title;
      if (prev.desc !== null) setMeta("description", prev.desc);
      if (prev.ogTitle !== null) setOg("og:title", prev.ogTitle);
      if (prev.ogDesc !== null) setOg("og:description", prev.ogDesc);
      if (prev.ogImage !== null) setOg("og:image", prev.ogImage);
      if (prev.ogUrl !== null) setOg("og:url", prev.ogUrl);
      if (prev.ogSite !== null) setOg("og:site_name", prev.ogSite);
    };
  }, [title, description, image, url]);
}

function getMeta(name, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  const el = document.querySelector(`meta[${attr}="${name}"]`);
  return el ? el.getAttribute("content") : null;
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setOg(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
